import type { NilDealResponse } from '@on3/api';
import { Check } from '@on3/ui-lib/components/Svg';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { ResizedImage } from 'components/Image/ResizedImage';
import { ArrowRight } from 'components/Svg/ArrowRight';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import { format } from 'date-fns';
import Link from 'next/link';

import styles from './DealTrackerItem.module.scss';
import { GroupDealItem } from './GroupDealItem';
import { getSourceUrl } from './helper';
import { PlayerDealItem } from './PlayerDealItem';

interface IDealResponse {
  item?: NilDealResponse;
}

export const DealTrackerItem = ({ item }: IDealResponse) => {
  const {
    date,
    verified,
    sourceUrl,
    company,
    amount,
    collectiveGroup,
    detail,
    type,
  } = item || {};
  const hasImage = collectiveGroup?.defaultAsset?.source;
  const showGroupItem = type === 'organization';
  const showPlayerItem = !showGroupItem;

  return (
    <li className={styles.container}>
      {showPlayerItem && <PlayerDealItem item={item} />}
      {showGroupItem && <GroupDealItem item={item} />}
      <div
        className={clsx(styles.trackerWrapper, {
          [styles.fullWidth]: !company?.name || !collectiveGroup?.name,
          [styles.fullHeight]: !detail?.title,
        })}
      >
        {(collectiveGroup || company) && (
          <div className={styles.dealPartners}>
            {collectiveGroup?.name && (
              <div className={styles.collectiveWrapper}>
                {collectiveGroup?.name && (
                  <Text className={styles.label} variant="subtitle1">
                    collective
                  </Text>
                )}
                {hasImage && collectiveGroup?.slug && (
                  <Link
                    href={`/nil/collectives/${collectiveGroup?.slug.toLowerCase()}/`}
                  >
                    <ResizedImage
                      className={styles.collectiveLogo}
                      {...collectiveGroup?.defaultAsset}
                      altText={`${collectiveGroup?.name} Logo`}
                      fit="cover"
                      gravity="0.5x0.5"
                      height={20}
                      quality={100}
                      width={96}
                    />
                  </Link>
                )}
                {collectiveGroup?.name && (
                  <TextLink
                    className={styles.collectiveLink}
                    href={`/nil/collectives/${collectiveGroup?.slug.toLowerCase()}/`}
                    underline="none"
                    variant="caption"
                  >
                    {collectiveGroup?.name}
                  </TextLink>
                )}
              </div>
            )}
            {collectiveGroup?.name && company?.name && (
              <div className={styles.median}>
                <div className={styles.left}>
                  <ArrowRight className={styles.arrow} />
                </div>
                <div className={styles.right}>
                  <ArrowRight className={styles.arrow} />
                </div>
              </div>
            )}
            {company?.name && (
              <div className={styles.companyWrapper}>
                <Text className={styles.label} variant="subtitle1">
                  Company/Client
                </Text>
                <Text variant="h5">{company?.name ?? 'No Company'}</Text>
              </div>
            )}
          </div>
        )}
        {detail?.title && detail?.fullUrl && (
          <TextLink
            className={styles.detailsLink}
            href={detail?.fullUrl}
            underline="none"
          >
            {detail?.title}
          </TextLink>
        )}
        <div className={styles.dealWrapper}>
          <div
            className={clsx(styles.dealContainer, {
              [styles.undisclosed]: !amount,
            })}
          >
            {verified && !!amount && (
              <div className={styles.checkmarkContainer}>
                <Check className={styles.checkmarkIcon} />
              </div>
            )}
            <Text className={styles.amount}>
              {!!amount && formatCurrency(amount)}
              {!amount && (
                <span className={styles.undisclosed}>Undisclosed</span>
              )}
            </Text>
          </div>
          <Text className={styles.date} variant="caption">
            {date ? format(new Date(date), 'M/dd/yy') : '-'}
          </Text>
          {sourceUrl && (
            <TextLink
              className={styles.source}
              href={sourceUrl}
              underline="none"
              variant="subtitle1"
            >
              {getSourceUrl(sourceUrl ?? '')}
              Source
            </TextLink>
          )}
        </div>
      </div>
    </li>
  );
};
